import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import styles from "./ExpenseTablePopup.module.css";
import logo from "../../../../img/logo rio verde pdf.png";
import logo4S from "../../../../img/Logo 4S.jpg";
import { API_BASE_URL } from "../../../../config";
import EditExpenseModal from "../EditExpenseModal/EditExpenseModal";
import { FaFilePdf, FaRegCreditCard, FaTrashAlt, FaPencilAlt, FaExclamationTriangle, FaDownload } from "react-icons/fa";
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { gerarOrdemPDF, gerarRelatorioPDF } from "./reportGeneratorExpense";

Chart.register(...registerables, ChartDataLabels);

const initialColumnSelection = {
    descricao: true,
    tipoDeDespesa: true,
    fornecedor: true,
    produto: true,
    unidade: true,
    quantidade: true,
    valorUnitario: true,
    total: true,
    vencimento: true,
};

const ChartComponent = ({ config }) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.destroy();
        }
        const canvas = canvasRef.current;
        if (canvas) {
            chartRef.current = new Chart(canvas, config);
        }
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, [config]);

    return <canvas ref={canvasRef}></canvas>;
};

const ExpenseTablePopup = ({ isOpen, imovelId, onClose }) => {
    const [despesas, setDespesas] = useState([]);
    const [imovel, setImovel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedForOrder, setSelectedForOrder] = useState([]);
    const [reportOptions, setReportOptions] = useState({
        startDate: "",
        endDate: "",
        columns: initialColumnSelection,
    });
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [expenseToEdit, setExpenseToEdit] = useState(null);
    const navigate = useNavigate();
    const chartContainerRef = useRef(null);
    const [historicoOrdensBanco, setHistoricoOrdensBanco] = useState([]);

    const sortedDespesas = useMemo(() => {
        return [...despesas].sort((a, b) => {
            const dateA = a.validade ? new Date(a.validade) : new Date(0);
            const dateB = b.validade ? new Date(b.validade) : new Date(0);
            return dateB - dateA;
        });
    }, [despesas]);

    const ordensSalvas = useMemo(() => {
        const grupos = historicoOrdensBanco.reduce((acc, item) => {
            const num = item.numero_ordem;
            if (!acc[num]) {
                acc[num] = {
                    numero: num,
                    fornecedor: item.fornecedor || "N/A",
                    total: 0,
                    ids: []
                };
            }
            acc[num].total += parseFloat(item.total) || 0;
            if (item.despesa_id && !acc[num].ids.includes(item.despesa_id)) {
                acc[num].ids.push(item.despesa_id);
            }
            return acc;
        }, {});
        return Object.values(grupos).sort((a, b) => b.numero - a.numero);
    }, [historicoOrdensBanco]);

    const expensesByType = useMemo(() => {
        return despesas.reduce((acc, despesa) => {
            const type = despesa.tipo_de_despesa || 'Não categorizado';
            if (!acc[type]) acc[type] = [];
            acc[type].push(despesa);
            return acc;
        }, {});
    }, [despesas]);

    const formatCurrency = useCallback((value) => {
        const number = parseFloat(String(value).replace(',', '.'));
        return isNaN(number)
            ? "R$ 0,00"
            : new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(number);
    }, []);

    const formatDate = useCallback((dateString) => {
        return dateString
            ? new Date(dateString).toLocaleDateString("pt-BR", { timeZone: "UTC" })
            : "N/A";
    }, []);

    const formatDecimal = useCallback((value) => {
        const number = parseFloat(String(value).replace(',', '.'));
        if (isNaN(number)) {
            return '0,00';
        }
        return number.toFixed(2).replace('.', ',');
    }, []);

    const fetchData = useCallback(async () => {
        if (!imovelId) return;

        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const axiosConfig = { headers: { 'Authorization': `Bearer ${token}` } };
            
            const [despesasRes, imovelRes, historicoRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/imoveis/${imovelId}/despesas`, axiosConfig).catch(err => {
                    if (err.response && err.response.status === 404) return { data: [] };
                    throw err;
                }),
                axios.get(`${API_BASE_URL}/api/imoveis/${imovelId}`, axiosConfig),
                axios.get(`${API_BASE_URL}/api/imoveis/${imovelId}/historico-ordens`, axiosConfig).catch(() => ({ data: [] })),
            ]);

            setDespesas(Array.isArray(despesasRes.data) ? despesasRes.data : []);
            setImovel(imovelRes.data);
            setHistoricoOrdensBanco(Array.isArray(historicoRes.data) ? historicoRes.data : []);
        } catch (err) {
            console.error("Erro ao buscar dados:", err);
            if (err.response && (err.response.status === 401 || err.response.status === 400)) {
                localStorage.removeItem('token');
                navigate('/');
            } else {
                const errorMessage = err.response?.data?.error || "Não foi possível carregar os dados das despesas.";
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, [imovelId, navigate]);

    useEffect(() => {
        if (isOpen) {
            fetchData();
        } else {
            setDespesas([]);
            setImovel(null);
            setLoading(true);
            setError(null);
            setSelectedForOrder([]);
            setReportOptions({ startDate: "", endDate: "", columns: initialColumnSelection });
            setShowDeleteConfirmation(false);
            setItemToDeleteId(null);
            setIsEditModalOpen(false);
            setExpenseToEdit(null);
        }
    }, [isOpen, fetchData]);

    const confirmDelete = (id) => {
        setItemToDeleteId(id);
        setShowDeleteConfirmation(true);
    };

    const cancelDelete = () => {
        setItemToDeleteId(null);
        setShowDeleteConfirmation(false);
    };

    const executeDelete = async () => {
        if (!itemToDeleteId) return;
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }
        try {
            await axios.delete(`${API_BASE_URL}/api/despesas/${itemToDeleteId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setDespesas((prev) => prev.filter((d) => d.id !== itemToDeleteId));
            setSelectedForOrder((prev) => prev.filter((id) => id !== itemToDeleteId));
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 400)) {
                localStorage.removeItem('token');
                navigate('/');
            } else {
                console.error("Erro ao excluir despesa:", err);
                setError(err.response?.data?.error || "Erro ao excluir a despesa.");
            }
        } finally {
            cancelDelete();
        }
    };

    const handleOpenEditModal = (despesa) => {
        setExpenseToEdit(despesa);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setExpenseToEdit(null);
    };

    const handleUpdateExpense = async (updatedExpenseData) => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }
        try {
            const { data: updatedExpense } = await axios.put(
                `${API_BASE_URL}/api/despesas/${updatedExpenseData.id}`,
                updatedExpenseData,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setDespesas((prev) => prev.map((d) => d.id === updatedExpense.id ? updatedExpense : d));
            handleCloseEditModal();
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 400)) {
                localStorage.removeItem('token');
                navigate('/');
            } else {
                console.error("Erro ao atualizar despesa:", err);
                setError(err.response?.data?.error || "Não foi possível atualizar a despesa.");
            }
        }
    };

    const handleSelectForOrder = (id) => {
        setSelectedForOrder((prev) =>
            prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
        );
    };

    const handleSelectAllForOrder = (e) => {
        setSelectedForOrder(e.target.checked ? sortedDespesas.map((d) => d.id) : []);
    };

    const handleReportOptionsChange = (e) => {
        const { name, value } = e.target;
        setReportOptions((prev) => ({ ...prev, [name]: value }));
    };

    const handleColumnSelectChange = (column) => {
        setReportOptions((prev) => ({
            ...prev,
            columns: { ...prev.columns, [column]: !prev.columns[column] },
        }));
    };

    const totalCusto = despesas.reduce((sum, d) => sum + (parseFloat(d.total) || 0), 0);
    const custoPorArvore = imovel?.num_arvores_remanescentes ? totalCusto / imovel.num_arvores_remanescentes : 0;
    const despesaPorHa = imovel?.area_plantio ? totalCusto / imovel.area_plantio : 0;
    const custoPorMuda = imovel?.num_arvores_plantadas ? totalCusto / imovel.num_arvores_plantadas : 0;

    const totalsByType = useMemo(() => {
        return Object.entries(expensesByType).reduce((acc, [type, despesasDoTipo]) => {
            acc[type] = despesasDoTipo.reduce((sum, d) => sum + (parseFloat(d.total) || 0), 0);
            return acc;
        }, {});
    }, [expensesByType]);

    const comparativeChartConfig = useMemo(() => ({
        type: 'bar',
        data: {
            labels: Object.keys(totalsByType),
            datasets: [{
                label: 'Total Gasto por Tipo (R$)',
                data: Object.values(totalsByType),
                backgroundColor: ['#2ecc71', '#3498db', '#9b59b6', '#f1c40f', '#e74c3c', '#34495e', '#1abc9c'],
                borderRadius: 4,
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: { callbacks: { label: (context) => formatCurrency(context.raw) } },
                datalabels: { display: false }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: { callback: (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(value) }
                }
            }
        }
    }), [totalsByType, formatCurrency]);

    const handleGerarOrdemPDF = async () => {
        if (selectedForOrder.length === 0) return;

        const token = localStorage.getItem('token');
        const axiosConfig = { headers: { 'Authorization': `Bearer ${token}` } };

        try {
            const resNumero = await axios.get(`${API_BASE_URL}/api/ordens/proximo-numero`, axiosConfig);
            const proximoNumeroValido = resNumero.data.proximoNumero;

            await axios.put(`${API_BASE_URL}/api/despesas/vincular-ordem`, {
                ids: selectedForOrder,
                numero_ordem: proximoNumeroValido
            }, axiosConfig);

            gerarOrdemPDF({
                selectedForOrder,
                despesas,
                imovel,
                formatCurrency,
                formatDate,
                formatDecimal,
                logo,
                logo4S,
                numeroOrdem: proximoNumeroValido
            });

            fetchData();
        } catch (err) {
            console.error("Erro ao processar sequencial da ordem:", err);
            alert("Erro ao obter número de ordem sequencial do servidor.");
        }
    };

    const handleReemitirOrdemAntigaPDF = (ordem) => {
        gerarOrdemPDF({
            selectedForOrder: ordem.ids,
            despesas,
            imovel,
            formatCurrency,
            formatDate,
            formatDecimal,
            logo,
            logo4S,
            numeroOrdem: ordem.numero
        });
    };

    const handleGerarRelatorioPDF = () => {
        const despesasParaRelatorio = despesas.filter(d => selectedForOrder.includes(d.id));
        if (despesasParaRelatorio.length === 0) {
            alert("Por favor, selecione ao menos uma despesa na tabela para gerar o relatório.");
            return;
        }
        gerarRelatorioPDF({
            reportOptions,
            despesas: despesasParaRelatorio,
            imovel,
            formatCurrency,
            formatDate,
            formatDecimal,
            logo,
            logo4S,
            chartContainerRef
        });
    };

    if (!isOpen) return null;

    return (
        <div className={styles.expenseModalOverlay} onClick={onClose}>
            <div className={styles.expenseModalContent} onClick={(e) => e.stopPropagation()}>
                <div ref={chartContainerRef} style={{ position: 'absolute', top: '-9999px', left: '-9999px', width: '1000px', height: '1000px' }}></div>

                <EditExpenseModal
                    isOpen={isEditModalOpen}
                    onClose={handleCloseEditModal}
                    expense={expenseToEdit}
                    onSave={handleUpdateExpense}
                />

                {showDeleteConfirmation && (
                    <div className={styles.deleteConfirmationOverlay} onClick={cancelDelete}>
                        <div className={styles.deleteConfirmationModal} onClick={(e) => e.stopPropagation()}>
                            <FaExclamationTriangle className={styles.warningIcon} />
                            <h3>Confirmar Exclusão</h3>
                            <p>Tem certeza que deseja excluir esta despesa? A ação não pode ser desfeita.</p>
                            <div className={styles.modalFooterStacked}>
                                <button className={styles.btnDangerStacked} onClick={executeDelete}>
                                    <FaTrashAlt /> Confirmar Exclusão
                                </button>
                                <button className={styles.btnLinkStacked} onClick={cancelDelete}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <header className={styles.expenseModalHeader}>
                    <h2 className={styles.expenseModalTitle}>Relatório de Despesas</h2>
                    <span className={styles.expenseModalSubtitle}>{imovel?.descricao || ""}</span>
                    <button className={styles.expenseModalCloseBtn} onClick={onClose}>&times;</button>
                </header>

                <main className={styles.expenseModalBody}>
                    {loading ? (
                        <p className={styles.expenseLoadingMessage}>Carregando...</p>
                    ) : error ? (
                        <p className={styles.expenseErrorMessage}>{error}</p>
                    ) : (
                        <>
                            <section className={styles.expenseSection}>
                                <h3 className={styles.expenseSectionTitle}>Resumo Financeiro</h3>
                                <table className={styles.expenseSummaryTable}>
                                    <thead>
                                        <tr>
                                            <th>Custo Total</th>
                                            <th>Custo / Árv. Remanescentes</th>
                                            <th>Despesa / Ha</th>
                                            <th>Custo por Muda</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td data-label="Custo Total">{formatCurrency(totalCusto)}</td>
                                            <td data-label="Custo / Árv. Rem.">{formatCurrency(custoPorArvore)}</td>
                                            <td data-label="Despesa / Ha">{formatCurrency(despesaPorHa)}</td>
                                            <td data-label="Custo por Muda">{formatCurrency(custoPorMuda)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>

                            <div className={styles.expenseDivider}></div>

                            {ordensSalvas.length > 0 && (
                                <>
                                    <section className={styles.expenseSection}>
                                        <h3 className={styles.expenseSectionTitle}>Ordens de Pagamento Emitidas (Histórico)</h3>
                                        <div className={styles.expenseTableResponsive}>
                                            <table className={styles.expenseTable} style={{ marginBottom: "10px" }}>
                                                <thead>
                                                    <tr>
                                                        <th>Nº da Ordem</th>
                                                        <th>Fornecedor de Ref.</th>
                                                        <th className={styles.textRight}>Valor Total da Ordem</th>
                                                        <th style={{ textAlign: "center" }}>Ação</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ordensSalvas.map((ordem) => (
                                                        <tr key={ordem.numero}>
                                                            <td style={{ fontWeight: "bold", color: "#16a085" }}>Ordem #{ordem.numero}</td>
                                                            <td>{ordem.fornecedor}</td>
                                                            <td className={styles.textRight} style={{ fontWeight: "bold" }}>{formatCurrency(ordem.total)}</td>
                                                            <td style={{ textAlign: "center" }}>
                                                                <button 
                                                                    className={`${styles.actionBtnExpense} ${styles.editBtn}`} 
                                                                    title="Baixar Segunda Via do PDF" 
                                                                    onClick={() => handleReemitirOrdemAntigaPDF(ordem)}
                                                                    style={{ backgroundColor: "#16a085", color: "#fff", border: "none", padding: "5px 10px", borderRadius: "4px", cursor: "pointer" }}
                                                                >
                                                                    <FaDownload /> Reemitir PDF
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </section>
                                    <div className={styles.expenseDivider}></div>
                                </>
                            )}

                            <section className={styles.expenseSection}>
                                <h3 className={styles.expenseSectionTitle}>Lista de Despesas</h3>
                                <div className={styles.expenseTableResponsive}>
                                    <table className={styles.expenseTable}>
                                        <thead>
                                            <tr>
                                                <th><input type="checkbox" onChange={handleSelectAllForOrder} checked={sortedDespesas.length > 0 && selectedForOrder.length === sortedDespesas.length} title="Selecionar Todos" /></th>
                                                <th>Descrição</th>
                                                <th>Tipo</th>
                                                <th>Fornecedor</th>
                                                <th>Produto</th>
                                                <th>Unidade</th>
                                                <th>Qtd.</th>
                                                <th>V. Unitário</th>
                                                <th>Total</th>
                                                <th>Vencimento</th>
                                                <th>Status Ordem</th>
                                                <th>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedDespesas.map((despesa) => (
                                                <tr key={despesa.id} className={selectedForOrder.includes(despesa.id) ? styles.selectedRow : ""}>
                                                    <td><input type="checkbox" checked={selectedForOrder.includes(despesa.id)} onChange={() => handleSelectForOrder(despesa.id)} /></td>
                                                    <td>{despesa.descricao}</td>
                                                    <td>{despesa.tipo_de_despesa}</td>
                                                    <td>{despesa.fornecedor}</td>
                                                    <td>{despesa.produto}</td>
                                                    <td>{despesa.unidade}</td>
                                                    <td className={styles.textRight}>{formatDecimal(despesa.quantidade)}</td>
                                                    <td className={styles.textRight}>{formatCurrency(despesa.valor_unitario)}</td>
                                                    <td className={`${styles.textRight} ${styles.totalCell}`}>{formatCurrency(despesa.total)}</td>
                                                    <td>{formatDate(despesa.validade)}</td>
                                                    <td style={{ fontSize: "0.85rem", fontWeight: "600", color: despesa.numero_ordem ? "#16a085" : "#7f8c8d" }}>
                                                        {despesa.numero_ordem ? `Vinculada à #${despesa.numero_ordem}` : "Sem Ordem"}
                                                    </td>
                                                    <td className={styles.actionsCell}>
                                                        <button className={`${styles.actionBtnExpense} ${styles.editBtn}`} title="Editar Despesa" onClick={() => handleOpenEditModal(despesa)}><FaPencilAlt /></button>
                                                        <button className={`${styles.actionBtnExpense} ${styles.deleteBtn}`} title="Excluir Despesa" onClick={() => confirmDelete(despesa.id)}><FaTrashAlt /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            <div className={styles.expenseDivider}></div>

                            <section className={styles.expenseSection}>
                                <h3 className={styles.expenseSectionTitle}>Opções de Relatório Geral</h3>
                                <p style={{ marginTop: '-0.8rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#6b7280' }}>Selecione os itens na tabela acima e defina o período para gerar o relatório.</p>
                                <div className={styles.expenseReportOptions}>
                                    <div className={styles.dateFilter}>
                                        <label>Período do Relatório:</label>
                                        <div className={styles.dateInputs}>
                                            <input type="date" name="startDate" value={reportOptions.startDate} onChange={handleReportOptionsChange} />
                                            <span>até</span>
                                            <input type="date" name="endDate" value={reportOptions.endDate} onChange={handleReportOptionsChange} />
                                        </div>
                                    </div>
                                    <div className={styles.columnFilter}>
                                        <label>Colunas Incluídas:</label>
                                        <div className={styles.columnCheckboxes}>
                                            {Object.keys(initialColumnSelection).map((col) => (
                                                <div className={styles.checkboxItem} key={col}>
                                                    <input type="checkbox" id={`col-${col}`} name={col} checked={reportOptions.columns[col]} onChange={() => handleColumnSelectChange(col)} />
                                                    <label htmlFor={`col-${col}`}>{col.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <div className={styles.expenseDivider}></div>

                            <section className={styles.expenseSection}>
                                <h3 className={styles.expenseSectionTitle}>Análise Comparativa por Tipo de Despesa (Todos os Itens)</h3>
                                <div className={styles.expenseTableResponsive} style={{ marginBottom: '2rem' }}>
                                    <table className={styles.summaryTable}>
                                        <thead>
                                            <tr>
                                                <th>Tipo de Despesa</th>
                                                <th className={styles.textRight}>Total</th>
                                                <th className={styles.textRight}>Percentual (%)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(totalsByType).map(([type, total]) => (
                                                <tr key={type}>
                                                    <td>{type}</td>
                                                    <td className={styles.textRight}>{formatCurrency(total)}</td>
                                                    <td className={styles.textRight}>
                                                        {totalCusto > 0
                                                            ? `${((total / totalCusto) * 100).toFixed(2).replace('.', ',')} %`
                                                            : '0,00 %'
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className={styles.summaryChartContainer} style={{ position: 'relative', height: '350px' }}>
                                    <ChartComponent config={comparativeChartConfig} />
                                </div>
                            </section>
                        </>
                    )}
                </main>

                <footer className={styles.expenseModalFooter}>
                    <button type="button" className={`${styles.modalBtnExpense} ${styles.btnCancelExpense}`} onClick={onClose}>Fechar</button>
                    <button type="button" className={`${styles.modalBtnExpense} ${styles.btnSecondaryExpense}`} onClick={handleGerarOrdemPDF} disabled={selectedForOrder.length === 0}><FaRegCreditCard /> Gerar Ordem</button>
                    <button
                        type="button"
                        className={`${styles.modalBtnExpense} ${styles.btnSuccessExpense}`}
                        onClick={handleGerarRelatorioPDF}
                        disabled={!reportOptions.startDate || !reportOptions.endDate || selectedForOrder.length === 0}
                    >
                        <FaFilePdf /> Gerar Relatório
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default ExpenseTablePopup;